import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/firebase';

interface SystemSettings {
  licenseDurationHours: number;
  sessionDurationHours: number;
  maxActivationsPerLicense: number;
  maxLogRetentionDays: number;
  autoCleanupEnabled: boolean;
  maintenanceMode: boolean;
  allowConcurrentSessions: boolean;
  requireHardwareFingerprint: boolean;
  enableLocationTracking: boolean;
  autoRevokeExpiredSessions: boolean;
}

export function Settings() {
  const { currentUser, logout } = useAuth();
  const [settings, setSettings] = useState<SystemSettings>({
    licenseDurationHours: 48,
    sessionDurationHours: 24,
    maxActivationsPerLicense: 3,
    maxLogRetentionDays: 30,
    autoCleanupEnabled: true,
    maintenanceMode: false,
    allowConcurrentSessions: false,
    requireHardwareFingerprint: true,
    enableLocationTracking: true,
    autoRevokeExpiredSessions: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const getAuthHeaders = async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get ID token:', error);
      }
    }
    
    return headers;
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        showMessage('success', 'Instellingen succesvol opgeslagen');
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      showMessage('error', 'Fout bij opslaan van instellingen');
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/export`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          format: 'json',
          includeData: ['licenses', 'activations', 'logs']
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `licentie-data-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        showMessage('success', 'Data succesvol geëxporteerd');
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      showMessage('error', 'Fout bij exporteren van data');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupLogs = async () => {
    if (!confirm('Weet je zeker dat je oude logs wilt opruimen? Deze actie kan niet ongedaan worden gemaakt.')) {
      return;
    }

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/cleanup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          action: 'cleanup_logs',
          olderThanDays: settings.maxLogRetentionDays
        })
      });

      if (response.ok) {
        const data = await response.json();
        showMessage('success', `${data.deletedCount || 0} oude log entries opgeruimd`);
      } else {
        throw new Error('Cleanup failed');
      }
    } catch (error) {
      showMessage('error', 'Fout bij opruimen van logs');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeExpiredSessions = async () => {
    if (!confirm('Weet je zeker dat je alle verlopen sessies wilt intrekken?')) {
      return;
    }

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/activations/revoke-expired`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        showMessage('success', `${data.revokedCount || 0} verlopen sessies ingetrokken`);
      } else {
        throw new Error('Revoke failed');
      }
    } catch (error) {
      showMessage('error', 'Fout bij intrekken van verlopen sessies');
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = () => {
    showMessage('success', 'Test notificatie succesvol verzonden!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
            Instellingen
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Beheer systeemconfiguratie en voorkeuren
          </p>
          <p className="text-sm text-gray-500">
            Configureer licentie instellingen, sessie beheer en onderhoudstaken
          </p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-8 p-4 rounded-xl border shadow-sm ${
            message.type === 'success' 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-800' 
              : 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="mr-2">{message.type === 'success' ? '✅' : '❌'}</span>
                <span>{message.text}</span>
              </div>
              <button
                onClick={() => setMessage(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* License & Session Configuration */}
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
            <div className="relative z-10">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="mr-3 text-2xl">🔧</span>
                Licentie & Sessie Configuratie
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Licentie Duur (uren)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="8760"
                    value={settings.licenseDurationHours}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      licenseDurationHours: parseInt(e.target.value) || 48 
                    }))}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Standaard geldigheid van nieuwe licenties (1-8760 uren)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Sessie Duur (uren)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={settings.sessionDurationHours}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      sessionDurationHours: parseInt(e.target.value) || 24 
                    }))}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Hoe lang een activatie sessie geldig blijft (1-168 uren)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Max Activaties per Licentie
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={settings.maxActivationsPerLicense}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      maxActivationsPerLicense: parseInt(e.target.value) || 3 
                    }))}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Standaard maximum aantal gelijktijdige activaties (1-10)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Log Bewaring (dagen)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={settings.maxLogRetentionDays}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      maxLogRetentionDays: parseInt(e.target.value) || 30 
                    }))}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Hoe lang API logs bewaard worden voor automatische opruiming
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Security & Behavior Settings */}
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
            <div className="relative z-10">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="mr-3 text-2xl">🔒</span>
                Beveiliging & Gedrag
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Gelijktijdige Sessies</h3>
                    <p className="text-xs text-gray-600">Sta meerdere actieve sessies per licentie toe</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.allowConcurrentSessions}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        allowConcurrentSessions: e.target.checked 
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Hardware Fingerprint Vereist</h3>
                    <p className="text-xs text-gray-600">Vereis unieke hardware identificatie</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.requireHardwareFingerprint}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        requireHardwareFingerprint: e.target.checked 
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Locatie Tracking</h3>
                    <p className="text-xs text-gray-600">Verzamel IP-gebaseerde locatie informatie</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enableLocationTracking}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        enableLocationTracking: e.target.checked 
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Auto-intrekken Verlopen Sessies</h3>
                    <p className="text-xs text-gray-600">Trek verlopen sessies automatisch in</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.autoRevokeExpiredSessions}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        autoRevokeExpiredSessions: e.target.checked 
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Automatische Opruiming</h3>
                    <p className="text-xs text-gray-600">Ruim oude logs automatisch op</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.autoCleanupEnabled}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        autoCleanupEnabled: e.target.checked 
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Onderhoudsmodus</h3>
                    <p className="text-xs text-gray-600">Schakel API tijdelijk uit voor onderhoud</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.maintenanceMode}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        maintenanceMode: e.target.checked 
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Settings Button */}
        <div className="mt-8 text-center">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 mx-auto"
          >
            <span>{saving ? '⏳' : '💾'}</span>
            <span>{saving ? 'Opslaan...' : 'Alle Instellingen Opslaan'}</span>
          </button>
        </div>

        {/* System Actions */}
        <div className="mt-8 bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
          <div className="relative z-10">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3 text-2xl">🛠️</span>
              Systeemacties
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="mr-2">📥</span>
                  Data Exporteren
                </h3>
                <p className="text-xs text-gray-600 mb-3">
                  Exporteer alle data naar JSON
                </p>
                <button
                  onClick={handleExportData}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <span>{loading ? '⏳' : '📥'}</span>
                  <span>{loading ? 'Bezig...' : 'Export'}</span>
                </button>
              </div>

              <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="mr-2">🧹</span>
                  Logs Opruimen
                </h3>
                <p className="text-xs text-gray-600 mb-3">
                  Verwijder oude logs ({settings.maxLogRetentionDays}+ dagen)
                </p>
                <button
                  onClick={handleCleanupLogs}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <span>{loading ? '⏳' : '🧹'}</span>
                  <span>{loading ? 'Bezig...' : 'Opruimen'}</span>
                </button>
              </div>

              <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="mr-2">🚫</span>
                  Verlopen Sessies
                </h3>
                <p className="text-xs text-gray-600 mb-3">
                  Trek alle verlopen sessies in
                </p>
                <button
                  onClick={handleRevokeExpiredSessions}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  <span>{loading ? '⏳' : '🚫'}</span>
                  <span>{loading ? 'Bezig...' : 'Intrekken'}</span>
                </button>
              </div>

              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="mr-2">🔔</span>
                  Test Notificatie
                </h3>
                <p className="text-xs text-gray-600 mb-3">
                  Test het notificatiesysteem
                </p>
                <button
                  onClick={handleTestNotification}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <span>🔔</span>
                  <span>Test</span>
                </button>
              </div>
            </div>

            <div className="mt-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <span className="mr-2">🚪</span>
                Account Beheer
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Log uit van het admin paneel
              </p>
              <button
                onClick={logout}
                className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span>🚪</span>
                <span>Uitloggen</span>
              </button>
            </div>
          </div>
        </div>

        {/* System Information */}
        <div className="mt-8 bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
          <div className="relative z-10">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3 text-2xl">ℹ️</span>
              Systeeminformatie
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <div className="text-2xl mb-2">🔐</div>
                <h3 className="text-sm font-semibold text-gray-900">Gebruiker</h3>
                <p className="text-xs text-gray-600 mt-1">{currentUser?.email || 'Onbekend'}</p>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="text-2xl mb-2">🌐</div>
                <h3 className="text-sm font-semibold text-gray-900">API Status</h3>
                <p className="text-xs text-green-600 mt-1 font-medium">Online</p>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                <div className="text-2xl mb-2">📊</div>
                <h3 className="text-sm font-semibold text-gray-900">Versie</h3>
                <p className="text-xs text-gray-600 mt-1">v1.0.0</p>
              </div>
              
              <div className="text-center p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200">
                <div className="text-2xl mb-2">⏱️</div>
                <h3 className="text-sm font-semibold text-gray-900">Sessie Duur</h3>
                <p className="text-xs text-gray-600 mt-1">{settings.sessionDurationHours}u</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Copyright Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          © 2025 S3e
        </p>
      </div>
    </div>
  );
}

export default Settings; 